import React, { useRef, useState, useMemo } from 'react'
import { usePopper } from 'react-popper'

import ActionButton from 'components/Buttons/ActionButton'
import Flex from 'components/Flex'

import useEventListener from 'hooks/use-event-listener'
import { FixMeAny } from 'types/global'

import { CustomMenuProps, PopOverItem, PopOverProps } from './types'
import { PopupContainer } from './styles'
import MenuList from './MenuList'
import SearchableMenuList from './SearchMenuList'
import { SELECT_ALL_VALUE } from './index'

type Props = PopOverProps & {
  targetRef?: Element | null | undefined
  onClose?: () => void
}

const PopOverMenu = ({
  allowedAutoPlacements = ['bottom-start', 'bottom-end', 'top-start', 'top-end'],
  customMenuList,
  maxWidth,
  isDisabled,
  isWithSelectAll,
  menuOffset = [0, 4],
  onChange: propOnchange,
  onSearch,
  onReset,
  onToggleMenu,
  options,
  placement = 'bottom-start',
  width,
  isVisible = true,
  isSelectedOnTop = false,
  isVirtualized = false,
  style: externalStyle,
  returnSelectAllAsEmpty = true,
  withApplyButton = false,
  targetRef,
  onClose,
  ...props
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null)
  const [internalValue, setInternalValue] = useState(props.value)
  const { attributes, styles, update } = usePopper(targetRef, menuRef, {
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements,
        },
      },
      {
        name: 'offset',
        options: {
          offset: menuOffset,
        },
      },
    ],
    placement,
  })

  const hasSubOptions = useMemo(() => options?.some((option) => option.options && option.options.length > 0), [options])

  const mode = props.isMultiple ? (hasSubOptions ? 'mixed' : 'multiple') : 'single'
  // mixed = first level is single select but sub options is multi select
  // mixed is true when isMultiple is true and has suboptions

  const optionLength = useMemo(
    () =>
      options?.reduce((acc, option) => {
        if (option.options) {
          return acc + option.options.length
        }
        return acc + 1
      }, 0) ?? 0,
    [options],
  )

  const calculatedOptions: PopOverItem[] = useMemo(() => {
    if (!options || options.length === 0) {
      return [
        {
          value: 'no_option',
          label: 'no_option',
          isDisabled: true,
          labelColor: 'gray5',
        },
      ]
    }

    const originalOptions = options
      .filter((i) =>
        isSelectedOnTop && Array.isArray(props.value)
          ? !i.isHidden && !props.value?.find((option) => option.value === i.value)
          : !i.isHidden,
      )
      .map((i) => {
        // Add select all for suboptions
        if (i.options && i.options.length > 0 && isWithSelectAll) {
          return {
            ...i,
            options: [
              {
                label: SELECT_ALL_VALUE.label,
                value: `${i.value}:${SELECT_ALL_VALUE.value}`,
                isRadio: true,
              },
              ...i.options,
            ],
          }
        }
        return i
      })

    return isWithSelectAll ? [SELECT_ALL_VALUE, ...originalOptions] : originalOptions
  }, [isWithSelectAll, isSelectedOnTop, options, props.value])

  const calculatedValue = useMemo(() => {
    const originalValue = withApplyButton ? internalValue : props.value

    if (!props.isMultiple) {
      const singleValue = Array.isArray(originalValue) ? originalValue[0] : originalValue
      if (isWithSelectAll) {
        return singleValue || SELECT_ALL_VALUE
      }
      return originalValue
    }

    const currentMultiValues: PopOverItem[] = originalValue
      ? Array.isArray(originalValue)
        ? originalValue
        : [originalValue]
      : []

    const isEmpty = !currentMultiValues || currentMultiValues.length === 0
    const isSelectedAll = optionLength === currentMultiValues.length

    const selected = options?.reduce((acc, curr) => {
      if (curr.options && curr.options.length > 0) {
        const subOptions = curr.options
        const selectedSubOptions = subOptions.filter((i) => currentMultiValues.some((v) => v.value === i.value))
        const isSelectedAllSubOptions = selectedSubOptions.length === subOptions.length
        const isSelectedSomeSubOptions = selectedSubOptions.length > 0 && !isSelectedAllSubOptions
        const includeParent = isSelectedSomeSubOptions || isSelectedAllSubOptions
        if (isWithSelectAll && isSelectedAllSubOptions) {
          const selectAllSub = {
            label: `${SELECT_ALL_VALUE.label}`,
            value: `${curr.value}:${SELECT_ALL_VALUE.value}`,
            isRadio: true,
          }
          return [...acc, curr, selectAllSub]
        }
        return includeParent ? [...acc, curr, ...selectedSubOptions] : [...acc, ...selectedSubOptions]
      }

      if (currentMultiValues.some((v) => v.value === curr.value)) {
        return [...acc, curr]
      }

      return acc
    }, [] as PopOverItem[])

    if (isWithSelectAll) {
      return isEmpty || isSelectedAll ? [SELECT_ALL_VALUE] : selected
    }

    return currentMultiValues
  }, [withApplyButton, internalValue, props.value, props.isMultiple, isWithSelectAll, optionLength, options])

  // FixMe: Since the event is binding to the window rather than the element, this has to be fixed.
  useEventListener('mousedown', (event) => {
    const el = menuRef

    if (!el || el.contains(event.target as Node) || ref.current?.contains(event.target as Node)) {
      return
    }

    onToggleMenu?.(false)
    onClose?.()
  })

  const handleOpen = async (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation()

    //https://stackoverflow.com/a/65602010
    await update?.()

    if (isDisabled) {
      return
    }

    if (onToggleMenu) {
      onToggleMenu(false)
    }

    onClose?.()
  }

  const onChange = (value: PopOverItem | PopOverItem[] | undefined) => {
    if (withApplyButton) {
      setInternalValue(value)
      return
    }

    propOnchange?.(value as FixMeAny)
    !props.isMultiple && handleOpen()
  }

  const handleSelect = (value: PopOverItem) => {
    const originalValue = withApplyButton ? internalValue : props.value

    const isInSelectedValue: boolean = Array.isArray(originalValue)
      ? !!originalValue.find((v) => v.value === value.value)
      : originalValue?.value === value.value

    const subOptions = options?.find((i) => i.value === value.value)?.options
    const isParent = subOptions && subOptions.length > 0

    if (mode === 'single') {
      const val = isInSelectedValue ? undefined : value

      if (isWithSelectAll) {
        if (isParent) {
          onChange(isInSelectedValue ? undefined : [value])
          return
        }
        if (value.label === SELECT_ALL_VALUE.label || !val) {
          onChange(undefined)
          return
        }
        onChange(val)
        return
      }

      onChange(val)
      return
    }

    const currentMultiValues: PopOverItem[] = originalValue
      ? Array.isArray(originalValue)
        ? originalValue
        : [originalValue]
      : []

    if (mode === 'mixed') {
      const selected = options?.find((v) => v.value === value.value) // Only find first level
      if (selected) {
        const newValue = selected.options && selected.options.length > 0 ? selected.options : [value]
        onChange(newValue)
        return
      }
    }

    // When parent selected
    if (isParent) {
      const selectedSubOptions = subOptions.filter((i) => currentMultiValues.map((v) => v.value).includes(i.value))
      const selectedAllSubOptions = selectedSubOptions.length === subOptions.length
      const selectedSomeSubOptions = selectedSubOptions.length > 0 && !selectedAllSubOptions

      if (selectedAllSubOptions || selectedSomeSubOptions) {
        // Remove all sub options
        const newValues = currentMultiValues.filter(
          (v) => v?.value && !Boolean(subOptions.map((i) => i.value).includes(v.value)),
        )
        return onChange(newValues)
      }

      const newValues = [...currentMultiValues, ...subOptions]

      const isNewValueSelectedAll = newValues.length === optionLength
      if (isNewValueSelectedAll && returnSelectAllAsEmpty) {
        return onChange(undefined)
      }

      return onChange(newValues)
    }

    const parent = calculatedOptions?.find((i) => i.options?.some((o) => o.value === value.value))
    const isSubOption = !!parent
    if (isSubOption) {
      const subOpts = parent?.options?.filter((i) => !i.value.includes(`:${SELECT_ALL_VALUE.value}`)) || []
      const selectedSubOptions = subOpts.filter((i) => currentMultiValues.map((v) => v.value).includes(i.value))
      const selectedAllSubOptions = selectedSubOptions.length === subOpts.length
      if (value.value.includes(`:${SELECT_ALL_VALUE.value}`)) {
        if (selectedAllSubOptions) return
        return onChange([...currentMultiValues, ...subOpts])
      }
      if (isWithSelectAll && selectedAllSubOptions) {
        // Filter out all selected sub options except itself
        const newValues = currentMultiValues.filter((v) => !selectedSubOptions.map((i) => i.value).includes(v.value))
        return onChange([...newValues, value])
      }
    }

    if (isWithSelectAll) {
      if (Array.isArray(value)) {
        return onChange(value)
      }

      const val = isInSelectedValue
        ? currentMultiValues.filter((v) => v.value !== value.value)
        : Array.isArray(currentMultiValues)
        ? [...currentMultiValues, value]
        : [value]

      const currentSelectAll: boolean = Array.isArray(calculatedValue)
        ? currentMultiValues.some((v) => v.value === SELECT_ALL_VALUE.value) || currentMultiValues.length === 0
        : calculatedValue?.value === SELECT_ALL_VALUE.value || !calculatedValue

      const newSelectedAll: boolean = value.value === SELECT_ALL_VALUE.value

      if (currentSelectAll) {
        return newSelectedAll ? onChange(undefined) : onChange([value])
      }

      const selectedSameLength = val.length === optionLength

      if (newSelectedAll || selectedSameLength) {
        if (returnSelectAllAsEmpty) {
          return onChange(undefined)
        }
        return onChange(val)
      }

      return onChange(val)
    }

    const val = isInSelectedValue
      ? currentMultiValues.filter((v) => v.value !== value.value)
      : [...currentMultiValues, value]
    return onChange(val)
  }

  const menuProps = {
    ...props,
    ...(onSearch && { onSearch }),
    ...(onReset && { onReset }),
    isMultiple: props.isMultiple || isWithSelectAll,
    onSelect: (value: PopOverItem) => handleSelect(value),
    minWidth: width,
    options: calculatedOptions,
    value: calculatedValue,
    isSelectedOnTop,
    isVirtualized,
  }

  return (
    <PopupContainer
      data-testid="popup-component"
      {...attributes.popper}
      isVisible={isVisible}
      maxWidth={maxWidth}
      ref={setMenuRef}
      style={{ ...styles.popper, ...externalStyle }}
    >
      {customMenuList && React.isValidElement(customMenuList) ? (
        React.cloneElement(customMenuList as React.ReactElement<CustomMenuProps>, {
          onToggleOpenMenu: handleOpen,
          umamiEvent: props.umamiEvent,
        })
      ) : props.isSearchable ? (
        <SearchableMenuList {...menuProps} />
      ) : (
        <MenuList {...menuProps} />
      )}
      {withApplyButton && (
        <Flex justifyContent="space-between" alignItems="center" padding="0.5rem">
          <ActionButton
            variant="cancel"
            onClick={() => {
              onClose?.()
              setInternalValue(props.value)
            }}
          >
            cancel_btn
          </ActionButton>
          <ActionButton
            variant="save"
            onClick={() => {
              propOnchange?.(internalValue as FixMeAny)
              onClose?.()
            }}
          >
            apply_btn
          </ActionButton>
        </Flex>
      )}
    </PopupContainer>
  )
}

export default PopOverMenu
